import type {
  CreatorLabSelectedContent,
  CreatorLabSelectedContentByType,
} from '@app/routes/content/domain';
import { TransactionState } from '@app/store/domain';

import {
  ApiContentRepurposingStatus,
  ContentGenerationAttributesByType,
  ContentGenerationStatus,
  ContentHistory,
  ContentRepurposingResponse,
  ContentType,
} from './domain';

type ContentRepurposingSlice =
  | ({ data: ContentRepurposingResponse | null } & TransactionState)
  | null
  | undefined;

export type CreatorLabLoadingState = {
  showLoading: boolean;
  showSpinner: boolean;
  titleKey: string;
  descriptionKey: string;
};

export const INITIALIZING_TITLE_KEY =
  'content-recording.lab.initializing-content-lab';
export const INITIALIZING_DESCRIPTION_KEY =
  'content-recording.lab.initializing-content-lab-description';
export const FAILURE_TITLE_KEY =
  'content-recording.lab.unable-to-initialize-title';
const FAILURE_TITLE_KEY_LEGACY =
  'content-recording.lab.unable-to-initialize-title-legacy';
export const FAILURE_DESCRIPTION_KEY =
  'content-recording.lab.unable-to-initialize-description';
export const INELIGIBLE_TITLE_KEY =
  'content-recording.lab.video-not-eligible-title';
const INELIGIBLE_TITLE_KEY_LEGACY =
  'content-recording.lab.video-not-eligible-title-legacy';
export const INELIGIBLE_DESCRIPTION_KEY =
  'content-recording.lab.video-not-eligible-description';
const GENERATING_TONE_TITLE_KEY_LEGACY =
  'content-recording.lab.generating-tone-title-legacy';
const GENERATING_TONE_DESCRIPTION_KEY_LEGACY =
  'content-recording.lab.generating-tone-description-legacy';
const GENERATING_TOPICS_TITLE_KEY_LEGACY =
  'content-recording.lab.generating-topics-title-legacy';
const GENERATING_TOPICS_DESCRIPTION_KEY_LEGACY =
  'content-recording.lab.generating-topics-description-legacy';

export const MATCHING_TONE_KEY = 'content-recording.lab.matching-tone';
export const TONE_MATCHED_KEY = 'content-recording.lab.tone-matched';
export const USING_DEFAULT_TONE_KEY =
  'content-recording.lab.using-default-tone';
export const FINDING_TOPICS_KEY = 'content-recording.lab.finding-topics';
export const FOUND_TOPICS_KEY = 'content-recording.lab.found-topics';
export const FAILED_TO_FIND_TOPICS_KEY =
  'content-recording.lab.failed-to-find-topics';
export const LOADING_COMPLETED_KEY = 'content-recording.lab.loading-completed';

const DEFAULT_LOADING_STATE: CreatorLabLoadingState = {
  showLoading: true,
  showSpinner: true,
  titleKey: INITIALIZING_TITLE_KEY,
  descriptionKey: INITIALIZING_DESCRIPTION_KEY,
} as const;

const FAILURE_LOADING_STATE: CreatorLabLoadingState = {
  showLoading: true,
  showSpinner: false,
  titleKey: FAILURE_TITLE_KEY_LEGACY,
  descriptionKey: FAILURE_DESCRIPTION_KEY,
} as const;

const INELIGIBLE_LOADING_STATE: CreatorLabLoadingState = {
  showLoading: true,
  showSpinner: false,
  titleKey: INELIGIBLE_TITLE_KEY_LEGACY,
  descriptionKey: INELIGIBLE_DESCRIPTION_KEY,
} as const;

const GENERATING_TONE_LOADING_STATE: CreatorLabLoadingState = {
  showLoading: true,
  showSpinner: true,
  titleKey: GENERATING_TONE_TITLE_KEY_LEGACY,
  descriptionKey: GENERATING_TONE_DESCRIPTION_KEY_LEGACY,
} as const;

const GENERATING_TOPICS_LOADING_STATE: CreatorLabLoadingState = {
  showLoading: true,
  showSpinner: true,
  titleKey: GENERATING_TOPICS_TITLE_KEY_LEGACY,
  descriptionKey: GENERATING_TOPICS_DESCRIPTION_KEY_LEGACY,
} as const;

const READY_STATE: CreatorLabLoadingState = {
  showLoading: false,
  showSpinner: true,
  titleKey: DEFAULT_LOADING_STATE.titleKey,
  descriptionKey: DEFAULT_LOADING_STATE.descriptionKey,
} as const;

const isProcessingState = (status: TransactionState['status']) =>
  status === 'idle' || status === 'processing';

export const getLabLoadingState = (
  contentRepurposingState: ContentRepurposingSlice,
): CreatorLabLoadingState => {
  if (!contentRepurposingState) {
    return DEFAULT_LOADING_STATE;
  }

  const { status, data } = contentRepurposingState;

  if (isProcessingState(status)) {
    return DEFAULT_LOADING_STATE;
  }

  if (status === 'failed') {
    return FAILURE_LOADING_STATE;
  }

  if (status !== 'success' || !data) {
    return DEFAULT_LOADING_STATE;
  }

  switch (data.eligibility) {
    case 'INELIGIBLE':
      return INELIGIBLE_LOADING_STATE;
    case 'PENDING_CHECKS':
      return DEFAULT_LOADING_STATE;
    case 'ELIGIBLE': {
      const toneStatus = data.toneOfVoice?.status ?? 'FINISHED';
      const topicsStatus = data.topics.status;

      if (topicsStatus === 'FAILED') return FAILURE_LOADING_STATE;

      if (['INITIAL', 'IN_PROGRESS'].includes(toneStatus))
        return GENERATING_TONE_LOADING_STATE;

      if (topicsStatus !== 'FINISHED') return GENERATING_TOPICS_LOADING_STATE;

      return READY_STATE;
    }
    default:
      return DEFAULT_LOADING_STATE;
  }
};

/**
 * Maps API status to frontend status format
 */
export const mapApiStatusToFrontend = (
  apiStatus: ApiContentRepurposingStatus,
): ContentGenerationStatus => {
  switch (apiStatus) {
    case 'INITIAL':
      return 'initial';
    case 'IN_PROGRESS':
      return 'in_progress';
    case 'FINISHED':
      return 'finished';
    case 'FAILED':
      return 'failed';
    default:
      return 'initial';
  }
};

/**
 * Gets translation key for content type
 */
export const getContentTypeTranslationKey = (
  contentType: ContentType,
): string => {
  switch (contentType) {
    case 'SOCIAL_POST':
      return 'social-media';
    case 'BLOG_POST':
      return 'blog';
    case 'EMAIL':
      return 'email';
    default:
      return 'social-media';
  }
};

/**
 * Converts ContentType enum to URL slug format
 * @example contentTypeToUrlSlug('SOCIAL_POST') => 'social-post'
 */
export const contentTypeToUrlSlug = (contentType: ContentType): string => {
  switch (contentType) {
    case 'SOCIAL_POST':
      return 'social-post';
    case 'BLOG_POST':
      return 'blog-post';
    case 'EMAIL':
      return 'email';
    default:
      return 'social-post';
  }
};

/**
 * Converts URL slug to ContentType enum
 * @example urlSlugToContentType('social-post') => 'SOCIAL_POST'
 * @param slug - URL slug or undefined
 * @returns ContentType, defaults to 'SOCIAL_POST' if invalid
 */
export const urlSlugToContentType = (slug: string | undefined): ContentType => {
  switch (slug) {
    case 'social-post':
      return 'SOCIAL_POST';
    case 'blog-post':
      return 'BLOG_POST';
    case 'email':
      return 'EMAIL';
    default:
      return 'SOCIAL_POST';
  }
};

/**
 * Validates if a string is a valid content type slug
 * @param slug - URL slug to validate
 * @returns true if slug is valid, false otherwise
 */
export const isValidContentTypeSlug = (slug: string | undefined): boolean => {
  return ['social-post', 'blog-post', 'email'].includes(slug || '');
};

/**
 * Creates default content generation attributes based on content type
 * @param contentType - The type of content (SOCIAL_POST, BLOG_POST, or EMAIL)
 * @returns Default attributes object for the given content type
 */
export const createDefaultContentGenerationAttributes = <
  TContentType extends ContentType,
>(
  contentType: TContentType,
): ContentGenerationAttributesByType[TContentType] => {
  if (contentType === 'EMAIL') {
    return {
      toneOfVoiceId: null,
      topicId: null,
      emailAudienceId: null,
      emailTypeId: null,
    } as ContentGenerationAttributesByType[TContentType];
  }

  return {
    toneOfVoiceId: null,
    topicId: null,
  } as ContentGenerationAttributesByType[TContentType];
};

/**
 * Creates default creator lab selected content based on content type
 * @param type - The type of content (SOCIAL_POST, BLOG_POST, or EMAIL)
 * @returns Default selected content object for the given content type
 */
export const createDefaultCreatorLabSelectedContent = <
  TType extends ContentType,
>(
  type: TType,
): CreatorLabSelectedContentByType[TType] => {
  switch (type) {
    case 'EMAIL':
      return {
        id: null, // TODO(content-lab): hydrate with last-viewed content id once history flow is wired
        type: 'EMAIL',
        attributes: createDefaultContentGenerationAttributes('EMAIL'),
      } as CreatorLabSelectedContentByType[TType];
    case 'BLOG_POST':
      return {
        id: null,
        type: 'BLOG_POST',
        attributes: createDefaultContentGenerationAttributes('BLOG_POST'),
      } as CreatorLabSelectedContentByType[TType];
    case 'SOCIAL_POST':
    default:
      return {
        id: null,
        type: 'SOCIAL_POST',
        attributes: createDefaultContentGenerationAttributes('SOCIAL_POST'),
      } as CreatorLabSelectedContentByType[TType];
  }
};

/**
 * Creates default content history for all content types
 * @returns Default content history object with empty history for each content type
 */
export const createDefaultContentHistory = (): {
  [K in ContentType]: ContentHistory;
} => ({
  SOCIAL_POST: {
    history: [],
    cursor: null,
    status: 'idle',
  },
  BLOG_POST: {
    history: [],
    cursor: null,
    status: 'idle',
  },
  EMAIL: {
    history: [],
    cursor: null,
    status: 'idle',
  },
});

/**
 * Type guard to check if content is EMAIL type
 * @param content - The content to check
 * @returns true if content is EMAIL type
 */
export function isEmailContent(
  content: CreatorLabSelectedContent,
): content is CreatorLabSelectedContentByType['EMAIL'] {
  return content.type === 'EMAIL';
}

/**
 * Type guard to check if content is SOCIAL_POST type
 * @param content - The content to check
 * @returns true if content is SOCIAL_POST type
 */
export function isSocialMediaContent(
  content: CreatorLabSelectedContent,
): content is CreatorLabSelectedContentByType['SOCIAL_POST'] {
  return content.type === 'SOCIAL_POST';
}

/**
 * Type guard to check if content is BLOG_POST type
 * @param content - The content to check
 * @returns true if content is BLOG_POST type
 */
export function isBlogContent(
  content: CreatorLabSelectedContent,
): content is CreatorLabSelectedContentByType['BLOG_POST'] {
  return content.type === 'BLOG_POST';
}

/**
 * Extracts plain text from HTML string by creating a temporary DOM element
 * @param html - HTML string to extract text from
 * @returns Plain text content without HTML tags
 */
export const extractPlainTextFromHtml = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Cleans HTML by removing background colors and other unwanted styles
 * @param html - HTML string to clean
 * @returns Cleaned HTML string without background styles and text colors
 */
export const cleanHtmlForClipboard = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove background colors and related styles from all elements
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    const htmlElement = element as HTMLElement;
    // Remove background-related styles
    htmlElement.style.removeProperty('background-color');
    htmlElement.style.removeProperty('background');
    // Remove color to allow text to adapt to destination document's color scheme
    htmlElement.style.removeProperty('color');
  });

  return tempDiv.innerHTML;
};
